import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { loginUser, signUpUser } from '../services/authServices';
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import SimpleReactValidator from 'simple-react-validator';
import { toast } from 'react-toastify';

const MemoizedParticles = React.memo(Particles);

const Login = () => {
  const { login, setLogin } = useContext(AppContext);
  const [user, setUser] = useState({});
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(true);
  const [cssLoaded, setCssLoaded] = useState(false);
  const [validatorCount, setValidatorCount] = useState(0);
  const forceUpdate = () => setValidatorCount(validatorCount + 1);
  const initial = new SimpleReactValidator({
    locale: "en",
    autoForceUpdate: { forceUpdate },
    validators: {
      matches_domain: {
        message: "The email must belong to your domain :attribute.",
        rule: (val, domain) => {
          const emailDomain = val.split("@")[1];
          return emailDomain === domain[0];
        },
        required: true,
      },
      custom_domain: {
        message: "Please use a business or custom domain email (not Gmail, Yahoo, etc.).",
        rule: (val) => {
          if (!val.includes("@")) return false;

          const emailDomain = val.split("@")[1].toLowerCase();

          // List of public/free providers you want to block
          const blockedDomains = [
            "gmail.com",
            "yahoo.com",
            "hotmail.com",
            "outlook.com",
            "aol.com",
            "icloud.com",
            "protonmail.com",
            "zoho.com",
            "gmx.com",
          ];

          return !blockedDomains.includes(emailDomain);
        },
        required: true,
      },
    },
  })
  const [validator, setValidator] = useState(initial);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (validator.allValid()) {
      const result = await loginUser(user);
      if (result.success) {
        setLogin({ ...login, isLogin: true, token: result.token, user: result.user, userRole: 0 });
        localStorage.setItem("token", result.token);
        navigate('/');
      } else {
        setError(result.error);
      }
    } else {
      validator.showMessages();
      forceUpdate();
    }
  };

  const handleSignUp = async (e) => {
    try {
      e.preventDefault();
      if (validator.allValid()) {
        const result = await signUpUser(user);
        if (result.success) {
          toast.success(result.message)
          setSelected(!selected)
          setUser({})
        } else {
          toast.error(result.message)
        }
      } else {
        validator.showMessages();
        forceUpdate();
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    });
  }, []);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "./assets/css/login.css";
    link.onload = () => {
      setCssLoaded(true);
    };
    document.head.appendChild(link);

    // Cleanup on unmount
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    if (login.isLogin) navigate("/dashboard");
  }, [login]);

  const particlesOptions = useMemo(() => ({
    fpsLimit: 120,
    interactivity: {
      events: {
        onHover: {
          enable: true,
          mode: "repulse",
        },
        resize: true,
      },
      modes: {
        push: {
          quantity: 4,
        },
        repulse: {
          distance: 100,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: "#9bf51a",
      },
      links: {
        color: "#9bf51a",
        distance: 150,
        enable: true,
        opacity: 1,
        width: 1,
      },
      move: {
        direction: "none",
        enable: true,
        outModes: {
          default: "bounce",
        },
        random: false,
        speed: 4,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 500,
        },
        value: 500,
      },
      opacity: {
        value: 0.5,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 5 },
      },
    },
    detectRetina: true,
  }), []);

  return (
    <>
      {cssLoaded && <section className="login_wrapper d-flex align-items-center">
        <div className="container">
          <div className="row g-0 justify-content-center">
            <div className="col-lg-4 p-0">
              {selected ? <div className="login-wrap text-center">
                <img src={"/assets/images/logo.png"} style={{ "width": "200px" }} alt="logo" />
                <form id="login-form" className='m-3' onSubmit={handleLogin}>
                  <div className="user-name">
                    <input type="text" name="email" id="email" onChange={(e) => { setUser({ ...user, "email": e.target.value }) }} placeholder="Email" />
                    <span className="text-danger small">{validator.message("Email", user.email, "required")}</span>
                  </div>
                  <div className="password mt-3">
                    <input type="password" name="password" id="password" onChange={(e) => { setUser({ ...user, "password": e.target.value }) }} placeholder="Password" />
                    <span className="text-danger small">{validator.message("Password", user.password, "required")}</span>
                  </div>
                  <div className="forgot-password">
                    <a onClick={() => { }}>Forgot Password?</a>
                  </div>
                  <div className="login-form-btn mt-5">
                    <a type="submit" onClick={handleLogin} className="form-btn">LOG IN</a>
                    <div className='mt-3' style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                      {error && <div><span id="ferror" className="alert alert-danger p-1 mt-3 fw-semibold" role="alert">
                        {error}
                      </span></div>}
                    </div>
                  </div>
                </form>
              </div> : <div className="login-wrap text-center">
                <img src={"/assets/images/logo.png"} style={{ "width": "200px" }} alt="logo" />
                <form id="login-form" className='m-3' onSubmit={handleSignUp}>
                  <div className="user-name">
                    <input type="text" name="name" id="name" onChange={(e) => { setUser({ ...user, "name": e.target.value }) }} placeholder="Name" />
                    <span className="text-danger small">{validator.message("Name", user.name, "required")}</span>
                  </div>
                  <div className="user-name mt-3">
                    <input
                      type="email"
                      name="email"
                      id="email"
                      onChange={(e) => {
                        setUser({ ...user, email: e.target.value });
                      }}
                      placeholder="Email"
                    />
                    <span className="text-danger small">
                      {validator.message(
                        "Email",
                        user.email,
                        `required|email|custom_domain|matches_domain:${user.domain}`
                      )}
                    </span>
                  </div>
                  <div className="user-name mt-3">
                    <input
                      type="text"
                      name="domain"
                      id="domain"
                      onChange={(e) => {
                        setUser({ ...user, domain: e.target.value });
                      }}
                      placeholder="Domain"
                    />
                    <span className="text-danger small">
                      {validator.message("Domain", user.domain, "required")}
                    </span>
                  </div>
                  <div className="user-name mt-3">
                    <input type="number" name="phone" id="phone" onChange={(e) => { setUser({ ...user, "phone": e.target.value }) }} placeholder="Phone" />
                    <span className="text-danger small">{validator.message("Phone", user.phone, "required")}</span>
                  </div>
                  <div className="user-name mt-3">
                    <input type="text" name="address" id="address" onChange={(e) => { setUser({ ...user, "address": e.target.value }) }} placeholder="Address" />
                    <span className="text-danger small">{validator.message("Address", user.address, "required")}</span>
                  </div>
                  <div className="password mt-3">
                    <input type="password" name="password" id="password" onChange={(e) => { setUser({ ...user, "password": e.target.value }) }} placeholder="Password" />
                    <span className="text-danger small">{validator.message("Password", user.password, "required")}</span>
                  </div>
                  <div className="password mt-3">
                    <input type="password" name="cpassword" id="password" onChange={(e) => { setUser({ ...user, "cpassword": e.target.value }) }} placeholder="Confirm password " />
                    <span className="text-danger small">{validator.message("Confirm Password", user.cpassword, "required")}</span>
                  </div>
                  <div className="login-form-btn mt-5">
                    <button type="submit" style={{ border: "none" }} onClick={handleSignUp} className="form-btn submit">Sign up</button>
                    <div className='mt-3' style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                      {error && <div><span id="ferror" className="alert alert-danger p-1 mt-3 fw-semibold" role="alert">
                        {error}
                      </span></div>}
                    </div>
                  </div>
                </form>
              </div>}
            </div>
            <div className="col-lg-4 p-0">
              <div className="grow-wrap text-center d-flex flex-column justify-content-center">
                <h2 className="mt-5 mb-4">Grow with Us !</h2>
                <p>To keep connected with us please login with your personal info</p>
                <div className="sign-us mt-5">
                  <a className="form-btn" onClick={() => setSelected(!selected)}>{selected ? "SIGN UP" : "Login"}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id='particles-js'>
          <MemoizedParticles
            id="tsparticles"
            options={particlesOptions}
          />
        </div>
      </section>}
    </>
  )
}

export default Login;